import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { DEFAULT_CLINIC_TIME_ZONE } from '#src/addressTimezone.ts'

export const MINIMUM_MEAL_PRESENCE_MINUTES = 30

const DAY_CENTER_MEALS = [
  {
    meal: 'breakfast',
    start: '08:00',
    end: '09:30',
    label: 'Breakfast (8:00 AM–9:30 AM)',
  },
  {
    meal: 'lunch',
    start: '11:30',
    end: '14:00',
    label: 'Lunch (11:30 AM–2:00 PM)',
  },
] as const satisfies readonly {
  meal: 'breakfast' | 'lunch'
  start: string
  end: string
  label: string
}[]

export interface MissedDayCenterMeal {
  meal: 'breakfast' | 'lunch'
  label: string
}

export interface DayCenterMealNotice {
  headline: string
  detail: string
}

const NEAR_CITIES = new Set([
  'alhambra',
  'monterey park',
  'rosemead',
  'san gabriel',
  'san marino',
  'temple city',
])

interface PresenceInterval {
  start: Date
  end: Date
}

export function missedDayCenterMeals(input: {
  appointmentStart: Date | string
  appointmentEnd: Date | string
  dayCenterStart: Date | string
  dayCenterEnd: Date | string
  clinicCity: string | undefined
  homeCity: string | undefined
  timeZone?: string
}): MissedDayCenterMeal[] {
  const timeZone = input.timeZone ?? DEFAULT_CLINIC_TIME_ZONE
  const start = toDate(input.appointmentStart)
  const end = toDate(input.appointmentEnd)
  const dayCenterStart = toDate(input.dayCenterStart)
  const dayCenterEnd = toDate(input.dayCenterEnd)
  if (
    [start, end, dayCenterStart, dayCenterEnd].some((date) =>
      Number.isNaN(date.getTime())
    )
  ) {
    return []
  }

  const day = formatInTimeZone(start, timeZone, 'yyyy-MM-dd')
  const isPmShift =
    formatInTimeZone(dayCenterStart, timeZone, 'HH:mm') >= '12:00'
  const travelMinutes = isNearCity(input.clinicCity) ? 15 : 30
  const departure = addMinutes(start, -(travelMinutes + 15))
  const arrival = addMinutes(end, travelMinutes)
  const lunchEnd = fromZonedTime(`${day}T14:00:00`, timeZone)
  // A returned AM rider stays through lunch service even when the calendar
  // block for their day-center transportation ends earlier.
  const mealServiceEnd = new Date(
    Math.max(dayCenterEnd.getTime(), lunchEnd.getTime())
  )

  // A PM rider pulled out before lunch ends is collected from home: near homes
  // are driven in for lunch first, far homes never reach the center.
  const collectedFromHome =
    isPmShift && departure >= dayCenterStart && departure < lunchEnd
  if (collectedFromHome && isNearCity(input.homeCity)) {
    return []
  }

  const intervals = collectedFromHome
    ? []
    : presenceIntervals({
        start,
        end,
        departure,
        arrival,
        dayCenterStart,
        dayCenterEnd: mealServiceEnd,
        isPmShift,
        clinicIsNear: isNearCity(input.clinicCity),
        returnCutoff: fromZonedTime(`${day}T11:30:00`, timeZone),
      })

  const missed: MissedDayCenterMeal[] = []
  for (const meal of DAY_CENTER_MEALS) {
    if (
      meal.meal === 'breakfast' &&
      (isPmShift ||
        formatInTimeZone(dayCenterStart, timeZone, 'HH:mm') > '09:00')
    ) {
      continue
    }
    const mealStart = fromZonedTime(`${day}T${meal.start}:00`, timeZone)
    const mealEnd = fromZonedTime(`${day}T${meal.end}:00`, timeZone)
    const presentMinutes = intervals.reduce(
      (total, interval) =>
        total + overlapMinutes(interval, { start: mealStart, end: mealEnd }),
      0
    )
    if (presentMinutes < MINIMUM_MEAL_PRESENCE_MINUTES) {
      missed.push({ meal: meal.meal, label: meal.label })
    }
  }
  return missed
}

export function dayCenterMealNotice(input: {
  dayCenterVisitFound: boolean
  missed: MissedDayCenterMeal[]
}): DayCenterMealNotice {
  if (!input.dayCenterVisitFound) {
    return {
      headline: 'No day-center meal at risk.',
      detail:
        'The participant has no day-center visit booked for the appointment date.',
    }
  }

  if (input.missed.length === 0) {
    return {
      headline: 'No day-center meal at risk.',
      detail: `The participant is expected to be at the center for at least ${MINIMUM_MEAL_PRESENCE_MINUTES} minutes during each applicable meal service.`,
    }
  }

  const names = joinMeals(input.missed.map((meal) => meal.meal))
  return {
    headline: `May miss ${names} at the day center.`,
    detail: `The visit and transportation leave fewer than ${MINIMUM_MEAL_PRESENCE_MINUTES} minutes at the center during ${input.missed
      .map((meal) => meal.label)
      .join(' and ')}.`,
  }
}

function presenceIntervals(input: {
  start: Date
  end: Date
  departure: Date
  arrival: Date
  dayCenterStart: Date
  dayCenterEnd: Date
  isPmShift: boolean
  clinicIsNear: boolean
  returnCutoff: Date
}): PresenceInterval[] {
  if (input.isPmShift && input.departure < input.dayCenterStart) {
    const afterVisit = interval(input.arrival, input.dayCenterEnd)
    return afterVisit ? [afterVisit] : []
  }

  const beforeVisit = interval(input.dayCenterStart, input.departure)
  if (input.isPmShift) {
    return beforeVisit ? [beforeVisit] : []
  }

  const returns =
    input.end < input.returnCutoff ||
    (input.clinicIsNear && input.end <= input.returnCutoff)
  const afterVisit = returns
    ? interval(input.arrival, input.dayCenterEnd)
    : undefined
  return [beforeVisit, afterVisit].filter(
    (value): value is PresenceInterval => value !== undefined
  )
}

function interval(start: Date, end: Date): PresenceInterval | undefined {
  if (start >= end) {
    return undefined
  }
  return { start, end }
}

function overlapMinutes(
  left: PresenceInterval,
  right: PresenceInterval
): number {
  const start = Math.max(left.start.getTime(), right.start.getTime())
  const end = Math.min(left.end.getTime(), right.end.getTime())
  return Math.max(0, end - start) / 60_000
}

function isNearCity(city: string | undefined): boolean {
  if (!city) {
    return false
  }
  return NEAR_CITIES.has(
    city
      .toLowerCase()
      .replace(/,\s*$/, '')
      .replace(/\s+ca\s*$/, '')
      .trim()
  )
}

function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60_000)
}

function joinMeals(meals: ('breakfast' | 'lunch')[]): string {
  if (meals.length <= 1) {
    return meals[0] ?? 'a meal'
  }
  return `${meals.slice(0, -1).join(', ')} and ${meals[meals.length - 1]}`
}

function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value)
}
